import { injectIntl, useIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { v1 } from 'uuid';
import { Checkbox } from 'semantic-ui-react';
import { localeData } from '../../locales/data';
import * as React from 'react';
import { useEffect } from 'react';
import debounce from 'lodash.debounce';
import Parser, { HTMLReactParserOptions } from 'html-react-parser';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { addModifiers } from '../../shared/helpers';
import { formatMessageHelper, FormattedMessageHelper } from '../../shared/helpers/internationalization';
import * as Immutable from 'immutable';
import { StringTMap } from '../../types';

import { Control, useController } from 'react-hook-form';
import { HookFieldError, useFieldError } from '../../containers/Account/FormFields/HookFieldSet';
import { useWizardFormController } from '../../containers/Account/wizard/formHelpers';
import { WizardControllerProps } from '../../containers/Account/FormFields/types';
import { UserAgreement } from './UserAgreementsTypes';
import { useSelector } from 'react-redux';
import { AltchaCaptchaPortalTarget } from '../../containers/Altcha/AltchaWidget';
import { agreementFormStageSelector } from '../../reducers/AccountReducer/AccountWizardSelector';
import { AGREEMENTS_FORM_STAGES } from '../../reducers/AccountReducer/InitialAccountWizardData';
import { isTextNode } from '../Learn/LearnBlocks/BlockText';

interface AgreementsListProps {
  isValidToAgree?: boolean;
  agreementsListValues: StringTMap<boolean>;
  agreementsList: Object[];
  handleChangeAgreements: (event: any, data: any) => void;
}

export interface AgreementsButtonsProps {
  handleClose?: () => void;
  handleLater?: () => void;
  handleAccepted?: () => void;
  handleReject?: () => void;
  isAgreementButtonVisible?: boolean;
  agreementsAccepted?: boolean;
  buttonAcceptedText?: string;
  buttonCloseText?: string;
  agreementId?: string;
  competitionId?: string;
}

const AgreementsListIntl = ({
  isValidToAgree,
  agreementsListValues,
  agreementsList,
  handleChangeAgreements,
  intl,
}: AgreementsListProps & InjectedIntlProps) => (
  <div className="agreements__form">
    {agreementsList.map((agreement: any) => (
      <div className="checkbox__container" key={`agreementsList-${v1()}`}>
        <Checkbox
          disabled={!isValidToAgree}
          name={agreement.name}
          onChange={handleChangeAgreements}
          checked={agreementsListValues[agreement.name]}
          label={intl.formatMessage({ id: agreement.label, defaultMessage: localeData.en[agreement.label] })}
        />
      </div>
    ))}
  </div>
);

export const UserAgreementsList = ({
  agreementsList,
  control,
}: {
  agreementsList: AgreementsListProps['agreementsList'];
  control: Control<any>;
}) => {
  return (
    <div className="agreements__form">
      {agreementsList.map((agreement: any, i) => (
        <div className="checkbox__container" key={agreement.name}>
          <AgreementCheckbox
            agreement={agreement}
            controllerProps={{ name: agreement.name, control, replaceValidations: agreement.validate }}
          />
        </div>
      ))}
    </div>
  );
};

const AgreementCheckbox = ({ agreement, controllerProps }: { controllerProps: WizardControllerProps; agreement: UserAgreement }) => {
  const controller = useWizardFormController({ ...controllerProps });

  const onChange = (_: any, { checked }: any) => {
    controller.field.onChange({ target: { value: checked } });
    controller.field.onBlur();
  };

  const intl = useIntl();

  const error = useFieldError({ control: controllerProps.control, name: controller.field.name });

  return (
    <div className={error ? 'agreement__field-error' : undefined} ref={controller.field.ref}>
      <Checkbox
        name={agreement.name}
        onChange={onChange}
        checked={!!controller.field.value}
        label={formatMessageHelper(intl, agreement.label, agreement.labelValues)}
      />
      <HookFieldError keepErrorSpace={false} control={controllerProps.control} controller={controller} />
    </div>
  );
};

export const AgreementsList = injectIntl(AgreementsListIntl);
const replaceMathExpression: HTMLReactParserOptions['replace'] = domNode => {
  if (isTextNode(domNode) && domNode.type === 'text' && domNode.data && domNode.data.includes('$$')) {
    const matchArray = domNode.data.match(/(.*)\$\$(.*)\$\$(.*)/);

    if (!matchArray) {
      return;
    }

    const formula = matchArray[2];
    const before = matchArray[1];
    const after = matchArray[3];
    if (!formula) {
      return;
    }

    return (
      <span>
        {before}
        <MathJaxContext version={3} renderMode="pre">
          <MathJax inline text={formula} typesettingOptions={{ fn: 'tex2chtml' }} />
        </MathJaxContext>
        {after}
      </span>
    );
  }
};

export const displayAgreementContent = (agreements: Immutable.Map<string, any>) =>
  agreements.get('content').map((agreement: Immutable.Map<string, any>, index: number) => {
    const type = agreement.get('type');
    const value = agreement.get('value');

    return type && type === 'TEXT' && value ? (
      <div key={`profile-agreement-${v1()}-${index}`}>{Parser(value, { replace: replaceMathExpression })}</div>
    ) : null;
  });
export const AgreementsButtons = ({
  handleAccepted,
  agreementsAccepted,
  handleClose,
  isAgreementButtonVisible,
  buttonAcceptedText = 'agreements.iAgree',
  buttonCloseText = 'agreements.close',
}: AgreementsButtonsProps) => (
  <div className="agreements__buttons">
    {isAgreementButtonVisible && (
      <button
        data-testid={'agreements-accept-button'}
        type={'submit'}
        disabled={!agreementsAccepted}
        onClick={handleAccepted}
        className={addModifiers('button', !agreementsAccepted ? ['disabled'] : [])}
      >
        <FormattedMessageHelper tagName="span" id={buttonAcceptedText} defaultMessage={localeData.en[buttonAcceptedText]} />
      </button>
    )}
    {handleClose && (
      <button type={'button'} onClick={handleClose} className="button button--secondary">
        <FormattedMessageHelper tagName="span" id={buttonCloseText} defaultMessage={localeData.en[buttonCloseText]} />
      </button>
    )}
  </div>
);

export const UserAgreementsButtons = ({ handleBack, control }: { handleBack: () => void; control: Control<any> }) => {
  const formStage = useSelector(agreementFormStageSelector);
  const buttonAcceptedText = formStage === AGREEMENTS_FORM_STAGES.SubmitAgreement ? 'agreements.iAgree' : 'userAgreement.next';

  const isReadController = useController({ control, name: IS_READ_AGREEMENT_FIELD });

  return (
    <div className="agreements__buttons">
      <div className={'agreements__text-error-mobile'}>
        <HookFieldError keepErrorSpace={false} control={control} controller={isReadController} />
      </div>
      <AltchaCaptchaPortalTarget />
      <div className={'agreements__buttons-container'}>
        <button type={'button'} onClick={handleBack} className="button button--secondary">
          <FormattedMessageHelper tagName="span" id={'agreements.back'} />
        </button>
        <button data-testid={'agreements-accept-button'} type={'submit'} className={'button agreements__buttons-accept'}>
          <FormattedMessageHelper tagName="span" id={buttonAcceptedText} />
        </button>
      </div>
    </div>
  );
};

const getIsScrolledToBottom = (target: HTMLDivElement): boolean => target.scrollTop >= target.scrollHeight - target.clientHeight - 50;

export function useIsReadAllContent(contentRef: React.RefObject<HTMLDivElement | null>) {
  const [isReadAllContent, setIsReadAllContent] = React.useState(false);

  useEffect(() => {
    if (!contentRef.current || isReadAllContent) {
      return;
    }

    const content = contentRef.current;
    if (getIsScrolledToBottom(content)) {
      setIsReadAllContent(true);

      return;
    }

    const handleScroll = debounce((event: Event) => {
      const target = event.target;
      if (target && getIsScrolledToBottom(target as HTMLDivElement)) {
        setIsReadAllContent(true);
      }
    }, 300);

    content.addEventListener('scroll', handleScroll);

    return () => content.removeEventListener('scroll', handleScroll);
  }, []);
  return { isReadAllContent };
}

export const IS_READ_AGREEMENT_FIELD = 'isReadAgreement';
